import { Virtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef, useState } from "react";
import type { GroupLogLine, LogLine } from "~/components/job/Job/Log/Output/VirtualisedLogOutput";

/**
 * Per-`lines` metadata that drives the sticky group header. Recomputed
 * whenever the flattened `lines` array changes (i.e. when a group is
 * expanded/collapsed), so it always reflects the rows actually rendered.
 */
export interface GroupStickyMeta {
  /** Indices into `lines` of every group header row, in flat order. */
  groupIndices: number[];
  /**
   * `true` at a group header index when that group has visible content
   * scrolling beneath it — i.e. it's expanded and its next row is one of
   * its own log lines rather than the following group header. Collapsed or
   * empty groups are ineligible: there's nothing to scroll past, so pinning
   * them would just stack an empty header over the content below.
   */
  eligibleByIndex: boolean[];
  /**
   * For each group header, the index of the next group header in flat order
   * (its "follower"), or -1 for the last group. The follower's arrival from
   * below is what pushes the pinned header up (iOS-contacts style) as one
   * group's section ends and the next begins.
   */
  followerByIndex: number[];
}

/**
 * Build {@link GroupStickyMeta} from the flattened log rows. Log groups are
 * flat (a header followed by its expanded lines, never nested), so a single
 * header is ever pinned and the follower of a group is simply the next group
 * header.
 */
export function buildGroupStickyMeta(lines: Array<LogLine | GroupLogLine>): GroupStickyMeta {
  const groupIndices: number[] = [];
  const eligibleByIndex = new Array<boolean>(lines.length).fill(false);
  const followerByIndex = new Array<number>(lines.length).fill(-1);

  let prevGroupIndex = -1;
  for (let index = 0; index < lines.length; index++) {
    if (lines[index].type !== "group") {
      continue;
    }

    if (prevGroupIndex >= 0) {
      followerByIndex[prevGroupIndex] = index;
    }

    groupIndices.push(index);

    const next = lines[index + 1];
    eligibleByIndex[index] = !!next && next.type !== "group";

    prevGroupIndex = index;
  }

  return { groupIndices, eligibleByIndex, followerByIndex };
}

/**
 * Find the group header that should currently be pinned, or -1 when none
 * should be.
 *
 * The pinned group is the last (deepest in flat order) header whose top has
 * scrolled to or above the pin line (`scrollOffset + offsetTop`). If that
 * header is ineligible (collapsed/empty), nothing pins: its section has no
 * content beneath it, and the previously pinned header has by then been
 * pushed fully off screen by this header arriving as its follower — so the
 * handoff to "nothing pinned" is seamless rather than a snap.
 *
 * Uses the virtualizer's measurements (which cover every row via estimates,
 * not just measured ones) so row starts are monotonic and the scan is sound
 * even for rows outside the rendered window.
 */
export function computeActiveLogGroupIndex(
  scrollOffset: number,
  offsetTop: number,
  groupIndices: number[],
  eligibleByIndex: boolean[],
  measurements: ReadonlyArray<{ start: number; size: number } | undefined>,
): number {
  const threshold = scrollOffset + offsetTop;

  for (let position = groupIndices.length - 1; position >= 0; position--) {
    const index = groupIndices[position];
    const measurement = measurements[index];
    if (!measurement) {
      continue;
    }
    if (measurement.start <= threshold) {
      return eligibleByIndex[index] ? index : -1;
    }
  }

  return -1;
}

interface Options {
  rowVirtualizer: Virtualizer<Element, Element>;
  meta: GroupStickyMeta;
  /** Distance from the scroll viewport's top edge to pin at. */
  offsetTop: number;
}

/**
 * Drive the sticky log group header from a single scroll listener on the
 * virtualizer's scroll element. The consumer computes the active index itself
 * during render (from the virtualizer's scroll offset) so the pinned row is
 * always correct for the current frame; this hook's only jobs are:
 *
 * 1. Force a re-render when the active header flips, since a flip can happen
 *    within the virtualizer's overscan window without changing its range (so
 *    the virtualizer wouldn't otherwise re-render and the consumer's render-
 *    time computation wouldn't re-run).
 * 2. Apply the iOS-contacts push: as the pinned header's follower (the next
 *    group header) approaches from below, pull the pinned header up by the
 *    overlap via the `--sticky-push-offset` CSS custom property (folded into
 *    the row's sticky `top`), written straight to the DOM so per-scroll
 *    updates don't re-render React.
 *
 * The push is written to the header located by its `data-index` — never to a
 * cached element reference. On the frame the active header flips, a cached ref
 * still points at the *outgoing* header (the re-render hasn't swapped it yet);
 * resetting its offset there snaps it from "pushed off screen" back to the pin
 * line for one frame, flashing over the incoming header. Targeting by index
 * leaves the outgoing header's offset untouched until it turns absolute.
 */
export function useStickyLogGroupHeader({ rowVirtualizer, meta, offsetTop }: Options): void {
  const [, forceRender] = useState(0);
  // Last active index seen by the scroll listener, for flip detection.
  const lastActiveRef = useRef(-1);
  const { groupIndices, eligibleByIndex, followerByIndex } = meta;

  useEffect(() => {
    // The log table itself is the scroll viewport (attached by the virtualizer
    // in a layout effect, so it's set by the time this runs).
    const scrollElement = rowVirtualizer.scrollElement;
    if (!scrollElement) {
      return;
    }

    const update = () => {
      const scrollOffset = scrollElement.scrollTop;
      const measurements = rowVirtualizer.measurementsCache;

      const active = computeActiveLogGroupIndex(
        scrollOffset,
        offsetTop,
        groupIndices,
        eligibleByIndex,
        measurements,
      );

      if (active !== lastActiveRef.current) {
        lastActiveRef.current = active;
        forceRender((version) => version + 1);
      }

      if (active < 0) {
        return;
      }

      // Locate the pinned header by index rather than a cached ref (see the
      // doc comment): on a flip the cached ref lags by a frame and resetting
      // its offset/attributes there causes a snap-back flash.
      const stickyEl = scrollElement.querySelector<HTMLElement>(`tr[data-index="${active}"]`);
      if (!stickyEl) {
        return;
      }

      const measurement = measurements[active];
      const headerHeight = measurement?.size ?? 0;
      const naturalY = (measurement?.start ?? 0) - scrollOffset;

      // The header is only "covering" content once it has scrolled above its
      // natural resting position (content is passing beneath it). At exactly
      // the pin line it's flush with the row below, so there's nothing to
      // divide yet — match the list view's shadow gating. Toggle the divider
      // via a data attribute (styled in CSS) so it stays theme-aware.
      if (naturalY < offsetTop) {
        stickyEl.setAttribute("data-covering", "");
      } else {
        stickyEl.removeAttribute("data-covering");
      }

      // iOS-contacts push: when the next group header intrudes into the
      // pinned header's slot, pull the pinned header up by the overlap so the
      // follower takes its place cleanly instead of sliding underneath.
      const followerIndex = followerByIndex[active];
      let pushOffset = 0;
      if (followerIndex >= 0) {
        const follower = measurements[followerIndex];
        if (follower) {
          const followerViewportY = follower.start - scrollOffset;
          const overlap = followerViewportY - (offsetTop + headerHeight);
          if (overlap < 0) {
            pushOffset = overlap;
          }
        }
      }

      if (pushOffset === 0) {
        stickyEl.style.removeProperty("--sticky-push-offset");
      } else {
        stickyEl.style.setProperty("--sticky-push-offset", `${pushOffset}px`);
      }
    };

    scrollElement.addEventListener("scroll", update, { passive: true });
    update();

    return () => {
      scrollElement.removeEventListener("scroll", update);
    };
  }, [rowVirtualizer, groupIndices, eligibleByIndex, followerByIndex, offsetTop]);
}

/**
 * Memoised {@link buildGroupStickyMeta} keyed on the `lines` array.
 */
export function useGroupStickyMeta(lines: Array<LogLine | GroupLogLine>): GroupStickyMeta {
  return useMemo(() => buildGroupStickyMeta(lines), [lines]);
}
